import React from 'react';
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import FormControlLabel from "@mui/material/FormControlLabel";
import Switch from "@mui/material/Switch";
import Div from "@jumbo/shared/Div";
import DatePicker from "../../../shared/DatePicker";
import { getAPIData, getAPIDataParams, postApi } from '../../../utils/apiFunction';
import { textfieldDateFormat } from '../../../utils/dateFunctions';
import useSwalWrapper from "@jumbo/vendors/sweetalert2/hooks";
import { ACCESS, ACCESS_SECTIONS } from '../../../utils/constants/access';
import { checkAccess } from '../../../utils/roleFunctions';
import SubmitButtonDisplayer from "../../../components/SubmitButtonDisplayer";
import ControlledTextField from "../../../components/ControlledTextField";
import SelectTextField from "../../../components/SelectTextField";
import { displayDozFormat } from "../../../utils/numberFunctions";

const NewBatchExternal = (props) => {
    const [completed, setCompleted] = React.useState(props.completed);
    const [error, setError] = React.useState(null);
    const [average, setAverage] = React.useState(null);
    const [total, setTotal] = React.useState(null);
    const [grade, setGrade] = React.useState(null);
    const [grades, setGrades] = React.useState(null);
    const [species, setSpecies] = React.useState(null);
    const [speciesList, setSpeciesList] = React.useState([]);
    const [defaultSpeciesId, setDefaultSpeciesId] = React.useState(null);
    const [unitType, setUnitType] = React.useState(null);
    const [unitTypes, setUnitTypes] = React.useState(null);
    const [supplier, setSupplier] = React.useState(null);
    const [isHatched, setIsHatched] = React.useState(false);
    const [loading, setLoading] = React.useState(false);
    const [date, setDate] = React.useState(new Date());
    const [cropDate, setCropDate] = React.useState(new Date());
    const [suppliers, setSuppliers] = React.useState([]);
    const Swal = useSwalWrapper();
    const requiredEditAccess = { key: ACCESS_SECTIONS.FARMS, value: ACCESS.EDIT };

    React.useEffect(() => {
        // Lookups do not require query parameters, so use getAPIData
        getAPIData('/Farms/Lookups/Grades', setGrades);
        getAPIData('/Farms/Lookups/UnitTypes', setUnitTypes);
        getAPIData('/Farms/Lookups/Species', (data) => {
            const list = Array.isArray(data)
                ? data
                    .filter((s) => !(s?.description || '').toLowerCase().includes('angasi'))
                    .sort((a, b) => a.description > b.description ? 1 : -1)
                : [];
            setSpeciesList(list);
            const defaultSpecies = list.find((s) => s && typeof s.description === 'string' && s.description.toLowerCase().includes('rock'));
            setDefaultSpeciesId(defaultSpecies?.id ?? null);
        });
        // Use OysterCloud suppliers exposed via GroupsController, and
        // exclude any suppliers that contain "AOC" in the name.
        getAPIData('/Farms/Groups/OysterCloudSuppliers', (data) => {
            if (!Array.isArray(data)) {
                setSuppliers([]);
                return;
            }
            const externals = data
                .filter(s => s && typeof s.name === 'string' && !s.name.toUpperCase().includes('AOC'))
                .sort((a, b) => a.name > b.name ? 1 : -1);
            setSuppliers(externals);
        });
    }, []);

    React.useEffect(() => {
        if (grade && species && grade.speciesId !== species.id) {
            setGrade(null);
        }
    }, [grade, species]);

    React.useEffect(() => {
        const isRock = species?.description?.toLowerCase().includes('rock');
        if (!isRock && isHatched) {
            setIsHatched(false);
        }
    }, [species, isHatched]);

    const filteredGrades = React.useMemo(() => {
        if (!grades) {
            return [];
        }

        let filtered = grades;
        if (isHatched) {
            filtered = filtered.filter((g) => g.isHatchery);
        }
        if (species) {
            filtered = filtered.filter((g) => g.speciesId === species.id);
        }
        return filtered;
    }, [grades, isHatched, species]);

    const validate = () => {
        const warningMessages = [];
        const errorMessages = [];

        if (!species) {
            errorMessages.push('You must select a species.');
        }
        if (!supplier) {
            errorMessages.push('You must select a supplier.');
        }
        if (!unitType) {
            errorMessages.push('You must select a unit type.');
        }
        if (!grade) {
            errorMessages.push('You must select a grade.');
        }
        if (!total || total <= 0) {
            errorMessages.push('Total units must be greater than 0.');
        }
        if (isHatched && (!average || average <= 0)) {
            errorMessages.push('Average per unit must be greater than 0 for hatched stock.');
        }

        const formatErrorMessage = (array) => {
            let errString = '';
            array.map((a) => {
                errString = errString + a + '<br/>';
                return null;
            });
            return errString;
        };

        if (errorMessages.length > 0) {
            Swal.fire({
                html: formatErrorMessage(errorMessages),
                icon: 'error',
                title: 'Oops...',
            });
        } else if (warningMessages.length > 0) {
            Swal.fire({
                html: formatErrorMessage(warningMessages),
                icon: 'warning',
                title: 'Oops...',
                showCancelButton: true,
                confirmButtonText: 'Yes, Continue',
                cancelButtonText: 'No, I\'ll edit it',
            }).then(result => {
                if (result.value) {
                    submit();
                }
            });
        } else {
            submit();
        }
    };

    const submit = async () => {
        setLoading(true);
        setCompleted(true);

        const body = {
            SupplierId: supplier?.id,
            UnitTypeId: unitType?.id,
            GradeId: grade?.id,
            Average: average || 0,
            WarehouseId: props.farm?.warehouseId,
            CropDate: textfieldDateFormat(cropDate),
            isHatched: isHatched,
            TotalUnits: total,
            Date: textfieldDateFormat(date),
            ExternalReceived: true
        };

        const res = await postApi('/Farms/Groups/Create', body);
        if (!res.ok) {
            setError('Error creating external stock batch.');
        }

        if (props.reload) {
            props.reload();
        }
        setLoading(false);
    };

    return (
        <Box
            component="form"
            sx={{
                '& .MuiTextField-root': { m: 1, width: '24ch' },
                width: '60%',
            }}
            noValidate
            autoComplete="off"
        >
            <Div>
                <Grid container spacing={0} mb={1}>
                    <Grid item xs={12} sm={12} md={6} lg={6} xl={6} >
                        <DatePicker date={cropDate} setDate={setCropDate} label="Crop Birth Date" disabled={completed} />
                    </Grid>
                    <Grid item xs={12} sm={12} md={6} lg={6} xl={6} >
                        <Typography variant={"h4"} >Batch Name</Typography>
                        <Typography variant={"h3"} >
                            {cropDate?.getFullYear() + (species?.description.toLowerCase().includes('rock') ? (isHatched ? '-HAT ' : '-WLD ') : '-PAC ') + (supplier ? supplier.name : '')}
                        </Typography>
                    </Grid>
                    <Grid item xs={12} >
                        <SelectTextField
                            title="Supplier"
                            state={supplier}
                            nameFunc={(gd) => gd.name}
                            list={suppliers}
                            setState={setSupplier}
                            disabled={completed}
                        />
                    </Grid>
                    <Grid item xs={12} sm={12} md={6} lg={6} xl={6} >
                        <SelectTextField
                            title="Species"
                            state={species}
                            nameFunc={(sp) => sp.description}
                            list={speciesList}
                            checkID={defaultSpeciesId}
                            setState={setSpecies}
                            disabled={completed}
                        />
                    </Grid>
                    <Grid item xs={12} sm={12} md={6} lg={6} xl={6} >
                        <SelectTextField
                            title="Grade"
                            state={grade}
                            nameFunc={(gd) => gd.description}
                            list={filteredGrades}
                            setState={setGrade}
                            disabled={completed}
                        />
                    </Grid>
                    <Grid item xs={12} sm={12} md={6} lg={6} xl={6} >
                        <FormControlLabel
                            control={<Switch checked={isHatched} onChange={(e) => setIsHatched(e.target.checked)} disabled={completed || !(species?.description?.toLowerCase().includes('rock'))} />}
                            label="Hatchery stock"
                            disabled={completed}
                        />
                    </Grid>
                    <Grid item xs={12} sm={12} md={6} lg={6} xl={6} >
                        <SelectTextField
                            title="Unit Type"
                            state={unitType}
                            nameFunc={(gd) => gd.description}
                            list={unitTypes}
                            checkID={unitTypes && !unitType
                                ? (unitTypes.find(t => t && typeof t.description === 'string' && t.description.toLowerCase() === 'bags (transport)')?.id)
                                : null}
                            setState={setUnitType}
                            disabled={completed}
                        />
                    </Grid>
                    <Grid item xs={12} sm={12} md={6} lg={6} xl={6} >
                        <ControlledTextField title="Total Units" state={total} setState={setTotal} disabled={completed} number />
                    </Grid>
                    <Grid item xs={12} sm={12} md={6} >
                        <Grid container spacing={0} mb={1}>
                            <Grid item xs={12} sm={12} md={6} lg={5} xl={3} >
                                <ControlledTextField title="Average (pcs) per Unit" state={average} setState={setAverage} disabled={completed} number width={'100px'} />
                            </Grid>

                            <Grid item xs={12} sm={12} md={6} lg={5} xl={3} >
                                <ControlledTextField title="Average (doz) per Unit" state={average / 12} setState={(val) => { setAverage(val * 12) }} disabled={completed} number width={'100px'} />
                            </Grid>
                        </Grid>
                    </Grid>
                    <Grid item xs={12}>
                        {total && average ? (
                            <Typography variant={"h5"}>
                                Total oysters (doz): {displayDozFormat(total * average, false)}
                            </Typography>
                        ) : null}
                    </Grid>
                    <Grid item xs={12}  >
                        <SubmitButtonDisplayer
                            completed={completed}
                            error={error}
                            loading={loading}
                            allowed={checkAccess(props.access, requiredEditAccess)}
                            submitFunc={validate}
                            handleClose={props.handleClose}
                        />
                    </Grid>
                </Grid>

            </Div>
        </Box>
    );
};

export default NewBatchExternal;
